import { useId, useState, type ReactNode } from "react";
import { LOCALES, type Locale } from "@nh/shared";
import { LocaleTabs, type LocaleTabState } from "@/features/admin/components";

const localeNames: Record<Locale, string> = {
  es: "Español",
  en: "English",
};

export interface ProductLocalePanelProps {
  /** Cómo está de lleno cada idioma. Se pinta EN la pestaña. */
  states: Record<Locale, LocaleTabState>;
  /** El contenido del idioma activo. */
  children: (locale: Locale) => ReactNode;
}

/**
 * Pestañas de idioma con su panel, para los pasos que se escriben dos
 * veces: la información, los beneficios y la ficha editorial.
 *
 * ── Por qué solo se pinta un idioma ─────────────────────────────────
 * Montar los dos y esconder uno con `hidden` deja dos campos "Nombre" en
 * el documento a la vez: un lector de pantalla los anuncia por duplicado
 * y un autocompletado escribe en el equivocado.
 *
 * Y lo escrito en el idioma cerrado NO se pierde: react-hook-form
 * conserva por omisión (`shouldUnregister: false`) los valores de los
 * campos que se desmontan y los repone al montar. Por eso se puede
 * escribir el inglés, volver al español y enviar con los dos.
 *
 * `key={active}` fuerza el remontaje al cambiar de pestaña: sin él,
 * React reutilizaría los mismos `<input>` cambiándoles el `name` por
 * debajo, y el valor del idioma anterior se quedaría escrito en pantalla
 * como si fuera del nuevo.
 */
export function ProductLocalePanel({ states, children }: ProductLocalePanelProps) {
  const panelId = useId();
  const [active, setActive] = useState<Locale>("es");

  return (
    <>
      <LocaleTabs
        panelId={panelId}
        // Dentro de una rejilla, un `flex` se estira: la píldora ocuparía
        // el ancho entero y dejaría medio metro de fondo vacío a la
        // derecha de "English".
        className="justify-self-start"
        active={active}
        onChange={(value) => setActive(value as Locale)}
        tabs={LOCALES.map((locale) => ({
          value: locale,
          label: localeNames[locale],
          state: states[locale],
        }))}
      />

      <div
        key={active}
        id={panelId}
        role="tabpanel"
        aria-labelledby={`${panelId}-tab-${active}`}
        className="grid gap-4"
      >
        {children(active)}
      </div>
    </>
  );
}
